import React, { useState, useEffect } from 'react';
import { User, Settings, Bell, Shield, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { TwoFactorSetup } from '../components/TwoFactorSetup';
import { getProfile, updateProfile, type Profile, isDemoAccount } from '../lib/auth';

function ProfilePage() {
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    display_name: '',
    bio: ''
  });

  useEffect(() => {
    loadProfile();
    checkTwoFactorStatus();
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if this is a demo account
      const demoAccount = isDemoAccount(user.email);
      setIsDemo(demoAccount);

      const { data, error } = await getProfile(user.id);
      if (error) throw error;

      setProfile(data);
      setFormData({
        name: data.name || '',
        email: data.email || user.email || '',
        phone: data.phone || '',
        display_name: data.display_name || '',
        bio: data.bio || ''
      });
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const checkTwoFactorStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Skip 2FA check for demo accounts
      if (isDemoAccount(user.email)) {
        setTwoFactorEnabled(false);
        return;
      }

      // Get the most recent preference record for the user
      const { data: preferences, error } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          console.warn('Multiple preference records found for user, using most recent');
        } else {
          console.error('Error fetching preferences:', error);
          return;
        }
      }

      setTwoFactorEnabled(
        preferences?.preferences?.two_factor_enabled ||
        preferences?.preferences?.totp_enabled ||
        false
      );
    } catch (error) {
      console.error('Error checking 2FA status:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Prevent demo account modification
      if (isDemoAccount(user.email)) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        return;
      }

      const { error } = await updateProfile(user.id, {
        ...profile,
        ...formData
      });

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-6 md:py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto md:mx-0">
              <User className="w-8 h-8 md:w-10 md:h-10 text-indigo-600" />
            </div>
            <div className="text-center md:text-left">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Your Profile</h1>
              <p className="text-gray-600">Manage your account and preferences</p>
              {isDemo && (
                <p className="text-sm text-yellow-600 mt-1">
                  Note: This is a demo account. Some features are limited.
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">
              Profile updated successfully!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="border-b pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
              <div className="grid gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isDemo}
                  />
                </div>
                <div>
                  <label htmlFor="display_name" className="block text-sm font-medium text-gray-700">
                    Display Name
                  </label>
                  <input
                    type="text"
                    id="display_name"
                    name="display_name"
                    value={formData.display_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isDemo}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isDemo}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={isDemo}
                  />
                </div>
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    rows={4}
                    value={formData.bio}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Tell us a bit about yourself..."
                    disabled={isDemo}
                  />
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Security</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-gray-500" />
                    <span>Two-Factor Authentication</span>
                  </div>
                  {!loading && (
                    isDemo ? (
                      <span className="text-gray-500 text-sm">Not available for demo accounts</span>
                    ) : twoFactorEnabled ? (
                      <span className="text-green-600 font-medium">Enabled</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowTwoFactorSetup(true)}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        Enable
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Preferences</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Bell className="w-5 h-5 text-gray-500" />
                    <span>Notification Settings</span>
                  </div>
                  <button 
                    type="button" 
                    className="text-indigo-600"
                    disabled={isDemo}
                  >
                    {isDemo ? 'Not available for demo' : 'Edit'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Settings className="w-5 h-5 text-gray-500" />
                    <span>Account Settings</span>
                  </div>
                  <button 
                    type="button" 
                    className="text-indigo-600"
                    disabled={isDemo}
                  >
                    {isDemo ? 'Not available for demo' : 'Edit'}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={saving || isDemo}
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : isDemo ? (
                  'Demo Account (Read Only)'
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>

        {showTwoFactorSetup && !isDemo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <TwoFactorSetup
                onComplete={() => {
                  setShowTwoFactorSetup(false);
                  checkTwoFactorStatus();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;